import findIndex from 'lodash/findIndex'
import React, { FC, forwardRef } from 'react'
import styled from 'styled-components'

import AngleRightIcon from '@Assets/SVG/AngleRight'
import { FormElementProps } from '@Components/FormElements/FormElementProps'
import { Adornment, BaseFormInput, BaseFormInputProps } from '@Components/FormElements/UI/BaseFormInput'
import { useFormElementState } from '@Components/FormElements/useFormElementState'
import { SVGIcon } from '@Components/Icons'

const IconWrapper = styled.div({
  pointerEvents: 'none',
  position: 'absolute',
  right: 10,
  top: '50%',
  transform: 'translateY(-50%) rotateZ(90deg)',
})

const StyledBaseInput = styled(BaseFormInput)({
  [Adornment]: {
    height: '100%',
    pointerEvents: 'none',
  },
})

export type SelectFieldOption = {
  key: string
  value: string
}

export type SelectFieldProps = Omit<FormElementProps, 'adornments' | 'size' | 'withoutLabelAnimation'>
& Pick<BaseFormInputProps, 'withoutLabelAnimation'>
& JSX.IntrinsicElements['select']
& {
  allowEmptySelection?: boolean
  options: SelectFieldOption[]
  unwrapped?: boolean
}

export const SelectField: FC<SelectFieldProps> = forwardRef(
  (
    {
      allowEmptySelection,
      withoutLabelAnimation,
      error,
      id,
      label,
      name,
      placeholder,
      variant,
      wrapperClassName,
      size = 1,
      unwrapped,
      options = [],
      value,
      ...props
    }: SelectFieldProps,
    ref,
  ) => {
    const { formActive, ...state } = useFormElementState<'select'>({ ...props })

    const selectedOptionExists = (findIndex(options, option => option.key === value)) > -1

    const field = (
      <select
        // In case we're in "unwrapped" mode, we should probably make sure the input is still labelled.
        aria-label={unwrapped ? label : undefined}
        id={id}
        name={name}
        ref={ref}
        size={size}
        value={value}
        {...props}
        {...state}
      >
        {placeholder && (
          <option
            disabled={selectedOptionExists && !allowEmptySelection}
            value=""
          >
            {placeholder}
          </option>
        )}
        {options.map((option): JSX.Element => <option key={option.key} value={option.key}>{option.value}</option>)}
      </select>
    )

    if (unwrapped) { return field }

    return (
      <StyledBaseInput
        adornments={{ end: size === 1 && <IconWrapper><SVGIcon icon={AngleRightIcon} size="15px" /></IconWrapper> }}
        error={error}
        formActive
        id={id}
        label={label}
        variant={variant}
        withoutLabelAnimation={withoutLabelAnimation}
        wrapperClassName={wrapperClassName}
        {...props}
      >
        {field}
      </StyledBaseInput>
    )
  },
)
